import type { Pattern } from '../../contexts/PatternContext'
import { CopilotTurnContext, DraftEnvelope } from '../types'

/** Type guard for tool_result.data payloads. Malformed data returns false —
 * callers surface a visible error chip rather than silently dropping. */
export const isDraftEnvelope = (data: unknown): data is DraftEnvelope => {
  if (!data || typeof data !== 'object') return false
  const d = data as Record<string, unknown>
  return (
    d.kind === 'gc.draft' &&
    d.v === 1 &&
    (d.entityType === 'pattern' || d.entityType === 'indicator') &&
    d.mode === 'replace' &&
    !!d.draft &&
    typeof d.draft === 'object'
  )
}

/** Runtime-only fields excluded from the upstream draft (mirrors hashPattern's
 * exclusions: versions metadata and volatile flags aren't part of the draft). */
export const stripRuntimeFields = (pattern: Pattern): Record<string, unknown> => {
  const { versions, active, starred, ...rest } = pattern as Record<string, any>
  return rest
}

const SENTINEL = '[CURRENT_DRAFT_STATE'

/**
 * AS-IS upstream lane: the live editor state rides inside the message string
 * under a sentinel block the transcript strips for display. Attach the draft
 * only when it changed since the last send (hash-gated by the caller).
 */
export const composeTurnMessage = (
  text: string,
  turnContext: CopilotTurnContext,
  attachDraft: boolean
): string => {
  if (!attachDraft) return text
  return `${text}\n\n${SENTINEL} entityType=${turnContext.entityType} revision=${turnContext.revision} — live editor state including manual edits; treat as the authoritative current ${turnContext.entityType}]\n${turnContext.draftJson}`
}

/** Display text of a (possibly composed) user message. */
export const extractUserText = (content: string): string => {
  const idx = content.indexOf(`\n\n${SENTINEL}`)
  return idx === -1 ? content : content.slice(0, idx)
}
